/**
 * TextEditor Component - SEO-friendly Rich Text Editor
 *
 * A wrapper around CKEditor with SEO features for Vietnamese content.
 * Provides:
 * - Image upload and validation
 * - SEO content formatting
 * - External image detection and download
 * - Content cleaning utilities
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CKEditor } from '@/components/base/CKEditor';
import { uploadApi } from '../../../context/api/uploadApi';
import { NotifyHelper } from '../../../utils/NotifyHelper';
import { useAuth } from '../../../utils/AuthProvider';
import { Sparkles, AlertTriangle, DownloadCloud } from 'lucide-react';
import './TextEditor.css';

type ITextEditorProps = {
	/** Editor name for form submission */
	name?: string;
	/** HTML content value */
	value: string;
	/** Callback when content changes */
	onValueChanged: (value: string) => void;
	/** Disable the editor */
	disabled?: boolean;
	/** Placeholder text */
	placeholder?: string;
	/** Custom class name */
	className?: string;
};

function isValidURL(url: string): boolean {
	const res = url.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
	return res !== null;
}

function getImages(stringHtml: string): string[] {
	const imgRex = /<img.*?src="(.*?)"[^>]+>/g;
	const images: string[] = [];
	let img: RegExpExecArray | null;
	while ((img = imgRex.exec(stringHtml))) {
		images.push(img[1]);
	}
	return images;
}

function extractHostname(url: string): string {
	if (!isValidURL(url)) return '';

	let hostname: string;

	if (url.indexOf('//') > -1) {
		hostname = url.split('/')[2];
	} else {
		hostname = url.split('/')[0];
	}

	hostname = hostname.split(':')[0];
	hostname = hostname.split('?')[0];

	return hostname;
}

const TextEditor = ({
	value,
	onValueChanged,
	disabled = false,
	placeholder,
	className = '',
}: ITextEditorProps) => {
	const [imagesCount, setImageCount] = useState(0);
	const { account } = useAuth();

	const cdnDomain = useMemo(() => {
		return account?.websiteConfig?.cdnDomain || '';
	}, [account]);

	// Check images when value changes
	useEffect(() => {
		checkImage();
	}, [value, cdnDomain]);

	const checkImage = useCallback(() => {
		const images = getImages(value);
		let count = 0;
		images?.forEach((item) => {
			const domain = extractHostname(item);
			if (domain.length > 0 && !domain.includes(cdnDomain)) {
				count++;
			} else {
				if (item.includes('data:image')) {
					count++;
				}
			}
		});
		setImageCount(count);
	}, [cdnDomain, value]);

	const handleFormatContent = useCallback(() => {
		if (!value) return;

		try {
			uploadApi.CleanAllStyleContentAsync({ contentHtml: value }).then((response) => {
				NotifyHelper.Success(response.message);
				if (response && response.isSuccess) {
					const outPutHtml = response?.result as string;
					if (outPutHtml && outPutHtml.length > 0) {
						onValueChanged(outPutHtml);
					}
				}
			});
		} catch (error) {
			console.error('Format content error:', error);
		}
	}, [value, onValueChanged]);

	const handleDownloadImageInContent = useCallback(() => {
		if (!value) return;

		try {
			uploadApi.DownloadImageInContentHtmlAsync({ contentHtml: value }).then((response) => {
				NotifyHelper.Success(response.message);
				if (response && response.isSuccess) {
					const outPutHtml = response?.result as string;
					if (outPutHtml && outPutHtml.length > 0) {
						onValueChanged(outPutHtml);
					}
				}
			});
		} catch (error) {
			console.error('Download image error:', error);
		}
	}, [value, onValueChanged]);

	return (
		<div className={`text-editor-container ${className}`}>
			<div className='text-editor-toolbar'>
				<button
					type='button'
					className='text-editor-action-btn text-editor-action-btn--primary'
					onClick={handleFormatContent}
					disabled={disabled || !value}
				>
					<Sparkles className='h-4 w-4' />
					Chuẩn định dạng SEO
				</button>

				{imagesCount > 0 && (
					<div className='text-editor-alert'>
						<span className='text-editor-alert-icon'>
							<AlertTriangle className='h-4 w-4' />
						</span>
						<span className='text-editor-alert-text'>
							Có <strong>{imagesCount}</strong> ảnh ngoài hệ thống
						</span>
						<button
							type='button'
							className='text-editor-action-btn text-editor-action-btn--warning'
							onClick={handleDownloadImageInContent}
						>
							<DownloadCloud className='h-4 w-4' />
							Lưu ảnh
						</button>
					</div>
				)}
			</div>

			<CKEditor
				className='text-editor-ckeditor'
				value={value}
				onChange={onValueChanged}
				disabled={disabled}
				placeholder={placeholder}
			/>
		</div>
	);
};

export default TextEditor;
